import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { 
  Server, Code, ShoppingCart, Cpu, 
  ArrowRight, CheckCircle, Award
} from 'lucide-react';

const HERO_BG = "https://images.unsplash.com/photo-1680992044138-ce4864c2b962?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxzZXJ2ZXIlMjByb29tJTIwZGF0YSUyMGNlbnRlciUyMGJsdWUlMjBsaWdodHxlbnwwfHx8fDE3NzE0MzA5NTN8MA&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "/naraze-logo.png";

export default function Home() {
  const { t, language } = useLanguage();
  const [clients, setClients] = React.useState([]);
  const [loadingClients, setLoadingClients] = React.useState(true);

  const services = [
    {
      icon: Server,
      key: 'maintenance',
      color: 'from-cyan-500 to-blue-600',
    },
    {
      icon: Code,
      key: 'software',
      color: 'from-emerald-500 to-teal-600',
    },
    {
      icon: ShoppingCart,
      key: 'hardware',
      color: 'from-violet-500 to-purple-600',
    },
    {
      icon: Cpu,
      key: 'iot',
      color: 'from-orange-500 to-red-600',
    },
  ];

  // Fetch companies on component mount
  React.useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const apiUrls = [
          'https://narazesolusiteknologi.id/api/companies.php',
          'https://naraze.business/api/companies.php',
          '/api/companies.php'
        ];

        let companiesData = null;
        for (const url of apiUrls) {
          try {
            const response = await fetch(url, { 
              mode: 'cors',
              cache: 'no-cache'
            });
            if (response.ok) {
              const responseData = await response.json();
              companiesData = Array.isArray(responseData) ? responseData : (responseData.data || null);
              if (companiesData && companiesData.length > 0) break;
            }
          } catch (e) {
            console.debug(`Companies API URL failed: ${url}`);
            continue;
          }
        }

        if (companiesData && Array.isArray(companiesData)) {
          setClients(companiesData);
        } else {
          // Fallback to default companies
          const defaultCompanies = [
            { name: 'Bina Desa', logo_url: '/images/companies/bina-desa-logo.png' },
            { name: 'ID COMM', logo_url: '/images/companies/id-comm-logo.png' },
            { name: 'IJSL', logo_url: '/images/companies/ijsl-logo.png' },
            { name: 'Qavah', logo_url: '/images/companies/qavah-logo.png' },
            { name: 'TBV', logo_url: '/images/companies/tbv-logo.png' },
            { name: 'Zerone', logo_url: '/images/companies/zerone-logo.svg' }
          ];
          setClients(defaultCompanies);
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        // Fallback to default companies
        const defaultCompanies = [
          { name: 'Bina Desa', logo_url: '/images/companies/bina-desa-logo.png' },
          { name: 'ID COMM', logo_url: '/images/companies/id-comm-logo.png' },
          { name: 'IJSL', logo_url: '/images/companies/ijsl-logo.png' },
          { name: 'Qavah', logo_url: '/images/companies/qavah-logo.png' },
          { name: 'TBV', logo_url: '/images/companies/tbv-logo.png' },
          { name: 'Zerone', logo_url: '/images/companies/zerone-logo.svg' }
        ];
        setClients(defaultCompanies);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchCompanies();
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center justify-center circuit-bg"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="Server Room"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
            >
              <img 
                src={LOGO_URL}
                alt="Naraze Logo"
                className="h-16 md:h-20 w-auto mb-6"
              />
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
            >
              <span className="gradient-text">{t('hero.tagline')}</span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base md:text-lg text-muted-foreground mb-8 max-w-2xl"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="hero-cta-primary"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-cyan glow-cyan-hover w-full sm:w-auto"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  variant="outline"
                  size="lg"
                  data-testid="hero-cta-secondary"
                  className="rounded-full px-8 py-6 text-lg font-medium border-primary/50 hover:bg-primary/10 w-full sm:w-auto"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-primary rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section 
        data-testid="services-preview"
        className="py-16 md:py-24 bg-secondary/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('services.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('services.subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  data-testid={`service-card-${service.key}`}
                  className="group relative overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 h-full"
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <service.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">
                      {t(`services.items.${service.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t(`services.items.${service.key}.desc`)}
                    </p>
                    <Link 
                      to="/services"
                      className="inline-flex items-center text-sm text-primary hover:underline"
                    >
                      {t('common.learnMore')}
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section 
        data-testid="about-preview"
        className="py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="text-primary font-medium mb-2 block">{t('about.title')}</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('about.subtitle')}</h2>
              <p className="text-muted-foreground mb-6">{t('about.description')}</p>
              <p className="text-muted-foreground mb-8">{t('about.philosophy')}</p>
              
              <div className="space-y-3">
                {t('about.missionItems').slice(0, 3).map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" className="inline-block mt-8">
                <Button 
                  variant="outline"
                  data-testid="about-cta"
                  className="rounded-full px-6"
                >
                  {t('common.learnMore')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden border border-border">
                <img 
                  src="https://images.unsplash.com/photo-1758873269317-51888e824b28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZyUyMGRpdmVyc2V8ZW58MHx8fHwxNzcxNDMwOTU4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Team Meeting"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">100%</p>
                    <p className="text-xs text-muted-foreground">
                      {language === 'id' ? 'Kepemilikan Saham' : 'Ownership'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section 
        data-testid="clients-section"
        className="py-16 bg-primary/5 border-y border-border"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-8"
          >
            <h3 className="text-xl font-semibold text-muted-foreground">
              {language === 'id' ? 'Dipercaya Oleh' : 'Trusted By'}
            </h3>
          </motion.div>
          <div className="flex flex-wrap justify-center items-stretch gap-6 md:gap-10">
            {clients.map((client, index) => {
              // Handle both object (with logo) and string (legacy) formats
              const clientName = typeof client === 'string' ? client : client.name;
              const logoUrl = typeof client === 'object' ? client.logo_url : null;
              const logoSrc = logoUrl && logoUrl.startsWith('/images/companies/')
                ? `${logoUrl}?v=20260509-1`
                : logoUrl;
              
              return (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-center w-32 md:w-40 h-20 md:h-24"
                >
                  {logoUrl ? (
                    <div className="flex items-center justify-center w-full h-full">
                      <img 
                        src={logoSrc}
                        alt={clientName}
                        className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-lg md:text-xl font-semibold text-foreground/80 hover:text-primary transition-colors">{clientName}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="cta-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border border-primary/30 p-8 md:p-12"
          >
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {language === 'id' 
                  ? 'Siap Mengembangkan Bisnis Anda?' 
                  : 'Ready to Grow Your Business?'}
              </h2>
              <p className="text-muted-foreground mb-8">
                {language === 'id'
                  ? 'Konsultasikan kebutuhan IT Anda dengan tim kami dan dapatkan solusi terbaik untuk bisnis Anda.'
                  : 'Consult your IT needs with our team and get the best solutions for your business.'}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="cta-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 glow-cyan glow-cyan-hover"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
              <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-primary blur-3xl" />
              <div className="absolute bottom-10 right-32 w-24 h-24 rounded-full bg-cyan-400 blur-2xl" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
